import React from "react";
import { Typography, useTheme, Stepper, Step, StepLabel } from "@mui/material";
import { GoDotFill } from "react-icons/go";
import { Stack, styled } from "@mui/system";
import { IoIosCheckmark } from "react-icons/io";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
// import { useTranslation } from "react-i18next";
import { PiDotsThreeOutlineFill } from "react-icons/pi";
import { MdError, MdWarning } from "react-icons/md";

const AutobiaStepper = ({ 
  currentStep, 
  steps, 
  orientation = "horizontal",
  variant = "default", // "default", "compact", "minimal"
  showLabels = true,
  showOptionalText = true,
  activeColor = "secondary",
  completedColor = "success"
}) => {
  //SECTION - general
  const theme = useTheme();
  const t = (key) => key; // Fallback translation function

  //SECTION - styles
  const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: orientation === "horizontal" ? 15 : 22,
    },
    [`&.${stepConnectorClasses.vertical}`]: {
      padding: 0,
      marginLeft: 15,
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundColor: theme.palette[completedColor]?.main || theme.palette.success.main,
        transition: "background-color 0.3s ease",
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundColor: theme.palette[completedColor]?.main || theme.palette.success.main,
        transition: "background-color 0.3s ease",
      },
    },
    [`& .${stepConnectorClasses.line}`]: {
      height: orientation === "horizontal" ? 2 : "auto",
      minHeight: orientation === "vertical" ? 40 : 2,
      border: 0,
      backgroundColor: theme.palette.gray?.g400 || theme.palette.grey[400],
      borderRadius: 1,
      transition: "all 0.3s ease",
    },
  }));

  const getStepSize = () => {
    switch (variant) {
      case "compact": return { width: 28, height: 28 };
      case "minimal": return { width: 24, height: 24 };
      default: return { width: 32, height: 32 };
    }
  };

  const ColorlibStepIconRoot = styled("div")(({ theme, ownerState }) => {
    const size = getStepSize();
    return {
      border: `2px solid ${theme.palette.gray?.g400 || theme.palette.grey[400]}`,
      zIndex: 1,
      color: "#fff",
      width: size.width,
      height: size.height,
      display: "flex",
      borderRadius: "50%",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
      backgroundColor: theme.palette.background?.paper || "#fff",
      transition: "all 0.3s ease",
      ...(ownerState.active && {
        backgroundColor: theme.palette[activeColor]?.main || theme.palette.secondary.main,
        border: `2px solid ${theme.palette[activeColor]?.main || theme.palette.secondary.main}`,
        transform: "scale(1.1)",
        boxShadow: `0 0 0 3px ${theme.palette[activeColor]?.light || theme.palette.secondary.light}40`,
      }),
      ...(ownerState.completed && {
        backgroundColor: theme.palette[completedColor]?.main || theme.palette.success.main,
        border: `2px solid ${theme.palette[completedColor]?.main || theme.palette.success.main}`,
      }),
      ...(ownerState.error && {
        backgroundColor: theme.palette.error?.main || theme.palette.error.main,
        border: `2px solid ${theme.palette.error?.main || theme.palette.error.main}`,
      }),
      ...(ownerState.warning && {
        backgroundColor: theme.palette.warning?.main || theme.palette.warning.main,
        border: `2px solid ${theme.palette.warning?.main || theme.palette.warning.main}`,
      }),
    };
  });

  //SECTION - functions
  const ColorlibStepIcon = (props) => {
    const { active, completed, className, icon } = props;
    const step = steps[props.stepIndex];
    const hasError = step?.status === "error";
    const hasWarning = step?.status === "warning";
    
    return (
      <ColorlibStepIconRoot
        ownerState={{ completed, active, error: hasError, warning: hasWarning }}
        className={className}
      >
        {hasError ? (
          <MdError color={"white"} size={variant === "minimal" ? "1rem" : "1.2rem"} />
        ) : hasWarning ? (
          <MdWarning color={"white"} size={variant === "minimal" ? "1rem" : "1.2rem"} />
        ) : completed ? (
          <IoIosCheckmark color={"white"} size={variant === "minimal" ? "1.5rem" : "2rem"} />
        ) : active ? (
          <PiDotsThreeOutlineFill color={"white"} size={variant === "minimal" ? "1rem" : "1.3rem"} />
        ) : icon ? (
          icon
        ) : (
          <GoDotFill color={theme.palette.gray?.g400 || theme.palette.grey[400]} size={variant === "minimal" ? "0.8rem" : "1rem"} />
        )}
      </ColorlibStepIconRoot>
    );
  };

  const currentStepIndex = Array.isArray(steps) 
    ? steps.findIndex((step) => step?.id === currentStep)
    : 0;

  return (
    <Stack width="100%">
      <Stepper
        alternativeLabel={orientation === "horizontal"}
        orientation={orientation}
        activeStep={currentStepIndex}
        connector={<ColorlibConnector />}
        sx={{
          ...(variant === "minimal" && {
            "& .MuiStepLabel-label": {
              fontSize: "0.875rem",
            }
          })
        }}
      >
        {steps?.map((step, index) => (
          <Step key={step?.id || index}>
            <StepLabel
              StepIconComponent={(props) => (
                <ColorlibStepIcon {...props} stepIndex={index} />
              )}
              sx={{
                "& .MuiStepLabel-label.Mui-disabled.MuiStepLabel-alternativeLabel": {
                  marginTop: "0.5rem",
                },
                "& .MuiStepLabel-label": {
                  fontWeight: index === currentStepIndex ? 600 : 400,
                  color: index === currentStepIndex 
                    ? theme.palette[activeColor]?.main || theme.palette.secondary.main
                    : "inherit",
                }
              }}
              error={step?.status === "error"}
            >
              {showLabels && (
                <Stack direction={"column"} gap={"0.25rem"}>
                  <Typography 
                    fontWeight={index === currentStepIndex ? 600 : 500} 
                    variant={variant === "minimal" ? "body2" : "body1"}
                    color={step?.status === "error" ? "error" : "inherit"}
                  >
                    {typeof step === "string" ? step : t ? t(step?.label || step?.status) : step?.label || step?.status}
                  </Typography>
                  {showOptionalText && step?.optional && (
                    <Typography
                      fontWeight={400}
                      fontSize={variant === "minimal" ? "0.6rem" : "0.75rem"}
                      color="text.secondary"
                    >
                      {t ? t("labels.optional") : "(Optional)"}
                    </Typography>
                  )}
                  {step?.description && variant !== "minimal" && (
                    <Typography
                      fontSize={"0.75rem"}
                      color="text.secondary"
                      sx={{ maxWidth: "120px", textAlign: "center" }}
                    >
                      {step.description}
                    </Typography>
                  )}
                </Stack>
              )}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Stack>
  );
};

export default AutobiaStepper;